import { useEffect } from "react";
import { AudioProvider, useStopAudio } from "../components/Audio";
import Feedback from "../components/Feedback";
import Frame from "../components/Frame";
import RetryPanel from "../components/RetryPanel";
import {
  CHOICE,
  COORDINATE_PLANE,
  COUNTING,
  EXPRESSION_ENTRY,
  MATCHING,
  NUMERIC_ENTRY,
  SEQUENCE,
  SORTING,
  TABLE_COMPLETION,
  TEXT_ENTRY,
  TRACING,
} from "../data/activityAdapter";
import { currentResponse } from "../data/responses";
import { readRuntime } from "../data/runtimeStore";
import ChoiceStep from "./ChoiceStep";
import CoordinatePlaneStep from "./CoordinatePlaneStep";
import ExpressionEntryStep from "./ExpressionEntryStep";
import NumericEntryStep from "./NumericEntryStep";
import TableCompletionStep from "./TableCompletionStep";
import CountingStep from "./CountingStep";
import DrawingStep from "./DrawingStep";
import MatchingStep from "./MatchingStep";
import SequenceStep from "./SequenceStep";
import SortingStep from "./SortingStep";
import TextEntryStep from "./TextEntryStep";

// Each primitive has one generic screen, chosen only by the step's type.
// Drawing and tracing share the canvas.
export const SCREENS = {
  [CHOICE]: ChoiceStep,
  [SEQUENCE]: SequenceStep,
  [MATCHING]: MatchingStep,
  [COUNTING]: CountingStep,
  [SORTING]: SortingStep,
  [NUMERIC_ENTRY]: NumericEntryStep,
  [EXPRESSION_ENTRY]: ExpressionEntryStep,
  [COORDINATE_PLANE]: CoordinatePlaneStep,
  [TABLE_COMPLETION]: TableCompletionStep,
  [TEXT_ENTRY]: TextEntryStep,
};

// Moving to another step silences whatever was playing.
const StopOnStep = ({ stepId }) => {
  const stop = useStopAudio();
  useEffect(() => stop, [stepId, stop]);
  return null;
};

// What a step's screen restores: unfinished work kept on this tablet first
// (it is newer than anything handed in), otherwise the latest response handed
// in. Only the current response is ever shown, never the history.
export const savedFor = (session, step) =>
  session.runtime?.[step.id] ??
  (session.attempt?.source === "local" ? null : readRuntime(session.attempt?.id, step.id)) ??
  currentResponse(session.attempt?.responses?.[step.id]);

// One step on screen. Every screen hands in a canonical response through
// `onDone`; it never decides whether that response is right or whether the
// learner moves on. `verdict` is the gateway's answer about the last response
// handed in on this step (see useAttemptSteps).
export const StepScreen = ({ session, step, readOnly = false }) => {
  const verdict = session.verdict?.stepId === step.id ? session.verdict : null;
  const busy = session.saving || Boolean(verdict?.moving) || readOnly;
  const saved = savedFor(session, step);
  const Screen = SCREENS[step.kind];
  return Screen ? (
    <Screen
      key={step.id}
      step={step}
      attemptId={session.attempt?.id}
      saving={busy}
      saved={saved}
      verdict={verdict}
      readOnly={readOnly}
      onDone={session.completeStep}
      onEstimate={session.saveProgress}
      onProgress={session.saveProgress}
    />
  ) : (
    <DrawingStep
      key={step.id}
      step={step}
      saved={saved}
      saving={busy}
      tracing={step.kind === TRACING}
      readOnly={readOnly}
      onDone={session.completeStep}
      onProgress={session.saveProgress}
      onUnavailable={session.unavailable}
    />
  );
};

// Only the task is on screen. A live lesson has no way out; homework offers
// one way back to the learner's list (`onBack`), and resumes where it was left.
const Activity = ({ session, onBack }) => {
  const { activity, stepIndex, saving, retry } = session;
  const step = activity.steps[stepIndex];
  const verdict = session.verdict?.stepId === step.id ? session.verdict : null;

  return (
    <Frame
      progress={{ current: stepIndex + 1, total: activity.steps.length }}
      onBack={onBack}
    >
      <AudioProvider>
        <StopOnStep stepId={step.id} />
        <StepScreen session={session} step={step} />
      </AudioProvider>
      <Feedback kind={verdict?.kind || null} />
      {retry && <RetryPanel onRetry={retry} saving={saving} />}
    </Frame>
  );
};

export default Activity;
